import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, Spinner, Alert, Badge, Input, Row, Col,
  Dropdown, DropdownToggle, DropdownMenu, DropdownItem,
  Nav, NavItem, NavLink, TabContent, TabPane,
} from 'reactstrap';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import FeatherIcon from 'feather-icons-react';
import api from '../helpers/api';
import usePermission from '../Hooks/usePermission';
import {
  TEAM_UPDATE, TEAM_DELETE, TEAM_MANAGE_MEMBERS,
  ASSET_UPDATE, LOCATION_UPDATE, ASSET_TYPE_UPDATE, CATEGORY_UPDATE,
} from '../constants/permissions';
import {
  fetchTeam, clearSelectedTeam, deleteTeam,
  addTeamMember, removeTeamMember, updateTeamMemberRole,
  fetchTeamResponsibilities, detachTeamResponsibility,
  clearTeamMessages,
} from '../store/team/actions';
import TeamResponsibilityPicker from './TeamResponsibilityPicker';

const ROLES = ['TEAM_ADMIN', 'TEAM_MEMBER'];

const TeamDetailModal = ({ isOpen, toggle, teamId, onEdit, onDeleted, initialTab = 'members', autoOpenAddMembers = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();
  const {
    selectedTeam: team,
    selectedTeamLoading: loading,
    selectedTeamError: error,
    memberSaving,
    memberError,
    saving,
    responsibilities,
    responsibilitiesLoading,
    responsibilitiesError,
    responsibilitySaving,
    responsibilityError,
  } = useSelector((s) => s.Team);
  const activeOrganizationId = useSelector((s) => s.ActiveContext?.activeOrganizationId);

  const [memberSearch, setMemberSearch] = useState('');
  const [orgUsers, setOrgUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [adderOpen, setAdderOpen] = useState(false);
  const [addingUserId, setAddingUserId] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [roleDropdown, setRoleDropdown] = useState({});
  const [activeTab, setActiveTab] = useState(initialTab);
  const [pickerType, setPickerType] = useState(null); // 'LOCATION' | 'ASSET' | 'ASSET_TYPE' | 'WORK_ORDER_CATEGORY'

  const canEdit = hasPermission(TEAM_UPDATE);
  const canDelete = hasPermission(TEAM_DELETE);
  const canManageGlobally = hasPermission(TEAM_MANAGE_MEMBERS);

  // Caller is a Team Admin of THIS team — service layer treats them as roster manager
  const currentUserId = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('authUser') || '{}')?.id; } catch { return null; }
  }, []);
  const callerIsTeamAdmin = useMemo(() => {
    if (!team || !currentUserId) return false;
    return (team.members || []).some((m) => m.userId === currentUserId && m.role === 'TEAM_ADMIN');
  }, [team, currentUserId]);
  const canManageMembers = canManageGlobally || callerIsTeamAdmin;

  useEffect(() => {
    if (isOpen && teamId) {
      dispatch(fetchTeam(teamId));
      dispatch(fetchTeamResponsibilities(teamId));
    }
    if (!isOpen) {
      setMemberSearch('');
      setAdderOpen(false);
      setOrgUsers([]);
      setConfirmDeleteOpen(false);
      setConfirmRemove(null);
      setActiveTab('members');
      setPickerType(null);
      dispatch(clearSelectedTeam());
      dispatch(clearTeamMessages());
    } else {
      // Honor caller's initial tab when (re-)opening (e.g. post-create flow).
      setActiveTab(initialTab);
      // Auto-open the add-member panel if requested (post-create flow). Loading
      // org users is async, so trigger the full openAdder() helper which fetches
      // them on first use.
      if (autoOpenAddMembers) {
        setAdderOpen(true);
        if (orgUsers.length === 0) fetchOrgUsers();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, teamId, dispatch, initialTab, autoOpenAddMembers]);

  const fetchOrgUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      // Scope to the active org. Without this, /user/get-all bleeds in users
      // from other orgs the caller has access to (super-admins typically),
      // and adding any of them server-rejects with team.error.user.not.in.org.
      const url = activeOrganizationId
        ? `user/get-all?organizationId=${activeOrganizationId}`
        : 'user/get-all';
      const { data } = await api.get(url);
      setOrgUsers(data?.response || []);
    } catch (e) {
      console.error('Failed to load users:', e);
      setOrgUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [activeOrganizationId]);

  const openAdder = () => {
    setAdderOpen(true);
    if (orgUsers.length === 0) fetchOrgUsers();
  };

  const memberUserIds = useMemo(
    () => new Set((team?.members || []).map((m) => m.userId)),
    [team?.members]
  );

  const candidateUsers = useMemo(() => {
    const term = memberSearch.trim().toLowerCase();
    return orgUsers
      .filter((u) => !memberUserIds.has(u.id))
      .filter((u) => {
        if (!term) return true;
        return (u.name || '').toLowerCase().includes(term)
          || (u.email || '').toLowerCase().includes(term);
      })
      .slice(0, 25);
  }, [orgUsers, memberUserIds, memberSearch]);

  const handleAddMember = (user, role) => {
    setAddingUserId(user.id);
    dispatch(addTeamMember(
      { teamId, userId: user.id, role },
      (err) => {
        setAddingUserId(null);
        if (!err) setMemberSearch('');
      }
    ));
  };

  const handleRoleChange = (member, newRole) => {
    if (member.role === newRole) return;
    dispatch(updateTeamMemberRole({ teamId, userId: member.userId, role: newRole }));
  };

  const handleRemove = () => {
    if (!confirmRemove) return;
    dispatch(removeTeamMember(teamId, confirmRemove.userId, (err) => {
      if (!err) setConfirmRemove(null);
    }));
  };

  const handleDelete = () => {
    dispatch(deleteTeam(teamId, (err) => {
      if (!err) {
        setConfirmDeleteOpen(false);
        toggle();
        if (onDeleted) onDeleted();
      }
    }));
  };

  const initials = (name) => (name || '').trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || '?';
  const roleLabel = (role) => role === 'TEAM_ADMIN' ? t('teams.role.admin', 'Team Admin') : t('teams.role.member', 'Member');

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        {team?.name || t('shared.loading', 'Loading...')}
      </ModalHeader>
      <ModalBody>
        {loading && (
          <div className="text-center py-4"><Spinner color="primary" /></div>
        )}
        {error && <Alert color="danger">{error}</Alert>}
        {memberError && <Alert color="danger">{memberError}</Alert>}

        {team && !loading && (
          <>
            <Row className="align-items-center mb-3">
              <Col xs="auto">
                <div
                  style={{
                    width: 64, height: 64, borderRadius: '50%',
                    background: team.photoUrl ? `url(${team.photoUrl}) center/cover no-repeat` : team.color,
                    color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 24, fontWeight: 600, border: `3px solid ${team.color}`,
                  }}
                >
                  {!team.photoUrl && initials(team.name)}
                </div>
              </Col>
              <Col>
                <h5 className="mb-1">{team.name}</h5>
                {team.description && <div className="text-muted small">{team.description}</div>}
                <div className="mt-1">
                  <Badge color="light" className="text-dark">
                    <FeatherIcon icon="users" size={12} className="me-1" />
                    {team.memberCount ?? (team.members?.length ?? 0)} {t('shared.members', 'members')}
                  </Badge>
                  {team.restrictVisibility && (
                    <Badge
                      color="light"
                      className="text-dark ms-2"
                      title={t('teams.restrictVisibility.help', "Members only see assets whose type is assigned to this team, and the work orders linked to them.")}
                    >
                      <FeatherIcon icon="lock" size={12} className="me-1" />
                      {t('teams.restrictVisibility.badge', 'Restricted')}
                    </Badge>
                  )}
                </div>
              </Col>
              {(canEdit || canDelete) && (
                <Col xs="auto">
                  {canEdit && (
                    <Button color="light" size="sm" className="me-2" onClick={() => onEdit && onEdit(team)}>
                      <FeatherIcon icon="edit-2" size={14} className="me-1" />
                      {t('shared.edit', 'Edit')}
                    </Button>
                  )}
                  {canDelete && (
                    <Button color="light" size="sm" className="text-danger" onClick={() => setConfirmDeleteOpen(true)}>
                      <FeatherIcon icon="trash-2" size={14} className="me-1" />
                      {t('shared.delete', 'Delete')}
                    </Button>
                  )}
                </Col>
              )}
            </Row>

            <hr />

            <Nav tabs className="mb-3">
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'members' })}
                  onClick={() => setActiveTab('members')}
                  style={{ cursor: 'pointer' }}
                >
                  <FeatherIcon icon="users" size={14} className="me-1" />
                  {t('shared.members', 'Members')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === 'responsibilities' })}
                  onClick={() => setActiveTab('responsibilities')}
                  style={{ cursor: 'pointer' }}
                >
                  <FeatherIcon icon="briefcase" size={14} className="me-1" />
                  {t('teams.responsibilities.title', 'Responsibilities')}
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab}>
              <TabPane tabId="members">

            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">{t('shared.members', 'Members')}</h6>
              {canManageMembers && !adderOpen && (
                <Button color="primary" size="sm" onClick={openAdder}>
                  <FeatherIcon icon="user-plus" size={14} className="me-1" />
                  {t('teams.members.add', 'Add member')}
                </Button>
              )}
            </div>

            {adderOpen && (
              <div className="border rounded p-2 mb-3 bg-light">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Input
                    type="text"
                    placeholder={t('teams.members.searchUsers', 'Search users by name or email...')}
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    style={{ maxWidth: 360 }}
                  />
                  <Button color="link" size="sm" onClick={() => setAdderOpen(false)}>
                    {t('shared.close', 'Close')}
                  </Button>
                </div>
                {usersLoading ? (
                  <div className="text-center py-3"><Spinner size="sm" /></div>
                ) : candidateUsers.length === 0 ? (
                  <div className="text-muted small px-2">
                    {t('teams.members.noCandidates', 'No matching users to add.')}
                  </div>
                ) : (
                  <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {candidateUsers.map((u) => (
                      <div key={u.id} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                        <div className="d-flex align-items-center gap-2">
                          {u.profileUrl
                            ? <img src={u.profileUrl} alt={u.name} width={32} height={32} className="rounded-circle" style={{ objectFit: 'cover' }} />
                            : <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>{initials(u.name)}</div>}
                          <div>
                            <div className="small fw-medium">{u.name}</div>
                            <div className="text-muted" style={{ fontSize: 11 }}>{u.email}</div>
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <Button color="light" size="sm" disabled={addingUserId === u.id || memberSaving} onClick={() => handleAddMember(u, 'TEAM_MEMBER')}>
                            {addingUserId === u.id ? <Spinner size="sm" /> : t('teams.role.member', 'Member')}
                          </Button>
                          <Button color="primary" outline size="sm" disabled={addingUserId === u.id || memberSaving} onClick={() => handleAddMember(u, 'TEAM_ADMIN')}>
                            {t('teams.role.admin', 'Team Admin')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {(team.members || []).length === 0 ? (
              <div className="text-muted text-center py-3">
                {t('teams.members.empty', 'No members yet')}
              </div>
            ) : (
              <div>
                {(team.members || []).map((m) => (
                  <div key={m.userId} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <div className="d-flex align-items-center gap-2">
                      {m.avatarUrl
                        ? <img src={m.avatarUrl} alt={m.fullName} width={36} height={36} className="rounded-circle" style={{ objectFit: 'cover' }} />
                        : <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>{initials(m.fullName)}</div>}
                      <div>
                        <div className="fw-medium">{m.fullName || `#${m.userId}`}</div>
                        <div className="text-muted small">{m.email}</div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {canManageMembers ? (
                        <Dropdown isOpen={!!roleDropdown[m.userId]} toggle={() => setRoleDropdown((s) => ({ ...s, [m.userId]: !s[m.userId] }))}>
                          <DropdownToggle caret color="light" size="sm">
                            <Badge color={m.role === 'TEAM_ADMIN' ? 'primary' : 'secondary'} pill className="me-1">
                              {roleLabel(m.role)}
                            </Badge>
                          </DropdownToggle>
                          <DropdownMenu container="body">
                            {ROLES.map((r) => (
                              <DropdownItem key={r} active={m.role === r} onClick={() => handleRoleChange(m, r)}>
                                {roleLabel(r)}
                              </DropdownItem>
                            ))}
                          </DropdownMenu>
                        </Dropdown>
                      ) : (
                        <Badge color={m.role === 'TEAM_ADMIN' ? 'primary' : 'secondary'} pill>
                          {roleLabel(m.role)}
                        </Badge>
                      )}
                      {canManageMembers && (
                        <Button color="link" size="sm" className="text-danger p-1" onClick={() => setConfirmRemove(m)} title={t('shared.remove', 'Remove')}>
                          <FeatherIcon icon="x" size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
              </TabPane>

              <TabPane tabId="responsibilities">
                <ResponsibilitiesPane
                  teamId={team.id}
                  responsibilities={responsibilities}
                  loading={responsibilitiesLoading}
                  error={responsibilitiesError}
                  saveError={responsibilityError}
                  saving={responsibilitySaving}
                  canEditAsset={hasPermission(ASSET_UPDATE)}
                  canEditLocation={hasPermission(LOCATION_UPDATE)}
                  canEditAssetType={hasPermission(TEAM_UPDATE)}
                  canEditWorkOrderCategory={hasPermission(CATEGORY_UPDATE)}
                  onAttach={(type) => setPickerType(type)}
                  onDetach={(type, item) =>
                    dispatch(
                      detachTeamResponsibility({
                        teamId: team.id,
                        targetType: type,
                        targetId: item.targetId,
                      })
                    )
                  }
                />
              </TabPane>
            </TabContent>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={toggle}>{t('shared.close', 'Close')}</Button>
      </ModalFooter>

      {/* Confirm Delete Team */}
      <Modal isOpen={confirmDeleteOpen} toggle={() => setConfirmDeleteOpen(false)} centered size="sm">
        <ModalHeader toggle={() => setConfirmDeleteOpen(false)}>{t('teams.delete.title', 'Delete team')}</ModalHeader>
        <ModalBody>
          <p>{t('teams.delete.confirm', 'Delete this team? Members will be unassigned.')}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setConfirmDeleteOpen(false)} disabled={saving}>
            {t('shared.cancel', 'Cancel')}
          </Button>
          <Button color="danger" onClick={handleDelete} disabled={saving}>
            {saving ? <Spinner size="sm" /> : t('shared.delete', 'Delete')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Confirm Remove Member */}
      <Modal isOpen={!!confirmRemove} toggle={() => setConfirmRemove(null)} centered size="sm">
        <ModalHeader toggle={() => setConfirmRemove(null)}>{t('teams.members.removeTitle', 'Remove member')}</ModalHeader>
        <ModalBody>
          <p>
            {t('teams.members.removeConfirm', 'Remove {{name}} from this team?', { name: confirmRemove?.fullName || '' })}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setConfirmRemove(null)} disabled={memberSaving}>
            {t('shared.cancel', 'Cancel')}
          </Button>
          <Button color="danger" onClick={handleRemove} disabled={memberSaving}>
            {memberSaving ? <Spinner size="sm" /> : t('shared.remove', 'Remove')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Responsibility picker */}
      {team && pickerType && (
        <TeamResponsibilityPicker
          isOpen={!!pickerType}
          toggle={() => setPickerType(null)}
          teamId={team.id}
          targetType={pickerType}
          attachedIds={
            new Set(
              (pickerType === 'LOCATION'
                ? responsibilities.locations
                : pickerType === 'ASSET'
                  ? responsibilities.assets
                  : pickerType === 'ASSET_TYPE'
                    ? responsibilities.assetTypes
                    : (responsibilities.workOrderCategories || [])
              ).map((r) => r.targetId)
            )
          }
        />
      )}
    </Modal>
  );
};

const ResponsibilitiesPane = ({
  teamId, // eslint-disable-line no-unused-vars
  responsibilities,
  loading,
  error,
  saveError,
  saving,
  canEditAsset,
  canEditLocation,
  canEditAssetType,
  canEditWorkOrderCategory,
  onAttach,
  onDetach,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner size="sm" />
      </div>
    );
  }

  return (
    <div>
      {error && <Alert color="warning">{error}</Alert>}
      {saveError && <Alert color="danger">{saveError}</Alert>}

      <ResponsibilitySection
        targetType="LOCATION"
        items={responsibilities.locations}
        title={t('teams.responsibilities.locations', 'Locations')}
        emptyText={t('teams.responsibilities.locationsEmpty', 'No locations assigned to this team yet.')}
        canEdit={canEditLocation}
        onAttach={() => onAttach('LOCATION')}
        onDetach={(item) => onDetach('LOCATION', item)}
        saving={saving}
      />

      <ResponsibilitySection
        targetType="ASSET"
        items={responsibilities.assets}
        title={t('teams.responsibilities.assets', 'Assets')}
        emptyText={t('teams.responsibilities.assetsEmpty', 'No assets assigned to this team yet.')}
        canEdit={canEditAsset}
        onAttach={() => onAttach('ASSET')}
        onDetach={(item) => onDetach('ASSET', item)}
        saving={saving}
      />

      <ResponsibilitySection
        targetType="ASSET_TYPE"
        items={responsibilities.assetTypes}
        title={t('teams.responsibilities.assetTypes', 'Asset types')}
        emptyText={t('teams.responsibilities.assetTypesEmpty', 'No asset types assigned to this team yet.')}
        canEdit={canEditAssetType}
        onAttach={() => onAttach('ASSET_TYPE')}
        onDetach={(item) => onDetach('ASSET_TYPE', item)}
        saving={saving}
      />

      <ResponsibilitySection
        targetType="WORK_ORDER_CATEGORY"
        items={responsibilities.workOrderCategories || []}
        title={t('teams.responsibilities.workOrderCategories', 'Work order categories')}
        emptyText={t('teams.responsibilities.workOrderCategoriesEmpty', 'No work order categories assigned to this team yet.')}
        canEdit={canEditWorkOrderCategory}
        onAttach={() => onAttach('WORK_ORDER_CATEGORY')}
        onDetach={(item) => onDetach('WORK_ORDER_CATEGORY', item)}
        saving={saving}
      />
    </div>
  );
};

const ResponsibilitySection = ({
  items, title, emptyText, canEdit, onAttach, onDetach, saving,
}) => {
  const { t } = useTranslation();
  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">{title}</h6>
        {canEdit && (
          <Button color="primary" outline size="sm" onClick={onAttach}>
            <FeatherIcon icon="plus" size={12} className="me-1" />
            {t('shared.add', 'Add')}
          </Button>
        )}
      </div>
      {(items || []).length === 0 ? (
        <div className="text-muted small px-2 py-2">{emptyText}</div>
      ) : (
        <div>
          {items.map((it) => (
            <div
              key={`${it.targetType}-${it.targetId}`}
              className="d-flex justify-content-between align-items-center py-2 border-bottom"
            >
              <div className="me-2" style={{ minWidth: 0 }}>
                <div className="fw-medium text-truncate" title={it.targetName}>
                  {it.targetName || '—'}
                </div>
                {it.targetSubtitle && (
                  <div className="text-muted small text-truncate" title={it.targetSubtitle}>
                    {it.targetSubtitle}
                  </div>
                )}
              </div>
              {canEdit && (
                <Button
                  color="link"
                  size="sm"
                  className="text-danger p-1"
                  disabled={saving}
                  onClick={() => onDetach(it)}
                  title={t('shared.remove', 'Remove')}
                >
                  <FeatherIcon icon="x" size={16} />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamDetailModal;
